import { useRef, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from './shared/RichTextContent';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  // Parallax refs
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <section ref={sectionRef} className="relative w-full h-screen overflow-hidden">
      {/* Parallax + Ken Burns animated background */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        {/* Oversized wrapper: 140 % tall starting 20 % above the section top,
            and ~2 % wider than the section (1 % bleed on each side). The
            extra height gives the parallax translate room to move, and the
            extra width absorbs the Ken Burns horizontal translate (-0.8 %)
            so no gap is exposed on the right edge. */}
        <div ref={imgWrapRef} className="absolute z-0 h-[140%]" style={{ top: '-20%', left: '-1%', right: '-1%', willChange: 'transform' }}>
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              priority
              sizes="100vw"
              className="w-full h-full object-cover"
              style={{
                animation: 'heroKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
                imageRendering: 'high-quality' as any,
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Soft full-bleed gradient (z-[1]) — same overlay settings as
          PinnedCallouts: kept light so the photo stays bright, with a faint
          center vignette so white headline/pre-title keep contrast on lighter
          images. Purely decorative; must not intercept clicks (floating
          chatbot iframes / widgets above the hero need to receive clicks
          through empty gradient areas). */}
      <div
        className="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-t from-black/22 via-black/8 to-black/4"
        aria-hidden="true"
      />
      <div
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse 70% 55% at 50% 50%, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.10) 45%, transparent 72%)',
        }}
        aria-hidden="true"
      />

      {/* Content. The flex container fills the section (h-full) so children
          can be vertically centered. padding-top matches the solid fixed
          header (--header-height from Header) so justify-center lands in
          the visible image area below the bar, not the full viewport.
          Fallback ~5rem covers the mobile nav row before measure runs.
          Pointer events stay off on the wrapper and on only on interactive
          children so empty hero space passes clicks through (e.g. RentCafe
          CRM IQ chatbot). */}
      <div
        className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8 pointer-events-none box-border"
        style={{ paddingTop: 'var(--header-height, 5rem)' }}
      >
        <RichTextContent
          path="hero.pre_title"
          label="Pre-Title"
          defaultValue='<p>Welcome to</p>'
          as="p"
          className="hero-entrance hero-entrance-1 hero-pre-title-shadow font-heading text-sm sm:text-base md:text-lg tracking-[0.25em] uppercase text-white mb-2 pointer-events-auto"
        />
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 hero-title-shadow font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-10 max-w-5xl leading-[1.1] tracking-tight text-white uppercase pointer-events-auto"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-3 hero-cta group/cta relative inline-flex items-center gap-2.5 px-10 py-4 text-sm font-medium tracking-[0.2em] uppercase text-white overflow-hidden transition-all duration-300 pointer-events-auto"
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.55s; }
        .hero-cta {
          /* Slight dark tint so the outlined (no-fill) CTA still reads
             clearly against the brightened hero image. */
          background: rgba(0, 0, 0, 0.18);
          border: 1.5px solid rgba(255, 255, 255, 0.75);
          border-radius: 2px;
          color: #fff;
          box-shadow: none;
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: var(--vibe-color-secondary);
          opacity: 0;
          transition: opacity 0.3s ease;
        }
        .hero-cta:hover {
          background: rgba(0, 0, 0, 0.18);
          border-color: var(--vibe-color-secondary);
          color: var(--vibe-color-text-on-secondary);
          box-shadow: 0 4px 16px color-mix(in srgb, var(--vibe-color-secondary) 30%, transparent);
          transform: translateY(-2px);
        }
        .hero-cta:hover::before {
          opacity: 1;
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        /* Soft dark shadows so white copy stays legible on bright
           photography without a heavy halo. */
        .hero-pre-title-shadow {
          text-shadow:
            0 1px 3px rgba(0, 0, 0, 0.28),
            0 2px 14px rgba(0, 0, 0, 0.18);
        }
        .hero-title-shadow {
          text-shadow:
            0 1px 3px rgba(0, 0, 0, 0.32),
            0 3px 18px rgba(0, 0, 0, 0.22);
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.pre_title': {
      label: 'Pre-Title',
      type: 'richtext',
      defaultValue: '<p>Welcome to</p>',
    },
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
  },
  slotGroups: {
    'hero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Property hero image',
        },
      },
    },
    'hero.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Availability',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
